import { useAuthState } from '@percent/cause-dashboard/common/hooks'
import { usePostHog } from 'posthog-js/react'
import { MutableRefObject, useEffect, useRef } from 'react'

const describeScript = (element: HTMLScriptElement) => ({
  connected: element.isConnected,
  parent: element.parentNode?.nodeName ?? null,
  type: element.type
})

const TermlyBanner = () => {
  const scriptRef = useRef<HTMLScriptElement>(null) as MutableRefObject<HTMLScriptElement>

  // The cleanup reads the client through a ref, kept current on every render, rather than as an effect
  // dependency: a new client reference must never re-run the effect and remove the scripts early.
  const posthog = usePostHog()
  const posthogRef = useRef(posthog)
  posthogRef.current = posthog

  const {
    authState: { user: userLoggedIn }
  } = useAuthState()

  useEffect(() => {
    if (userLoggedIn) {
      const acceptButton = document.querySelector('[data-tid="banner-accept"]')
      if (acceptButton instanceof HTMLElement) {
        acceptButton.click()
      }
      return
    }

    if (scriptRef.current) {
      return
    }

    const body = document.body

    const script = document.createElement('script')
    script.src = 'https://app.termly.io/resource-blocker/8cbb43ca-0785-4379-bf5d-be3ab7e572d7?autoBlock=on'
    script.async = true
    scriptRef.current = script
    body.appendChild(script)

    const termlyOptsScript = document.createElement('script')
    termlyOptsScript.type = 'text/javascript'
    termlyOptsScript.innerHTML =
      'window.TERMLY_CUSTOM_BLOCKING_MAP = { "goodstack.io": "essential", "poweredbypercent.com": "essential"  }'
    body.appendChild(termlyOptsScript)

    // remove() rather than removeChild(): something in some users' browsers detaches these scripts first,
    // and a throw here unmounts the whole app into the root error boundary.
    return () => {
      // TODO: temporary, tells us what detaches them. Remove once the cause is known.
      if (script.parentNode !== body || termlyOptsScript.parentNode !== body) {
        posthogRef.current?.capture('termly_scripts_detached', {
          bodyReplaced: document.body !== body,
          resourceBlocker: describeScript(script),
          blockingMap: describeScript(termlyOptsScript)
        })
      }

      script.remove()
      termlyOptsScript.remove()
    }
  }, [userLoggedIn])

  return null
}

export default TermlyBanner
